import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  IonGrid, IonRow, IonCol, IonCard, IonButtons, IonButton, IonIcon,
  IonList, IonItem, IonLabel, IonInput, IonSpinner,
} from "@ionic/react";
import { ellipsisVerticalOutline, cameraOutline, trashOutline } from "ionicons/icons";
import AppLayout from "@/components/AppLayout";
import AppSeo from "@/components/AppSeo";
import EditSheet from "@/components/EditSheet";
import FreshPopover from "@/components/FreshPopover";
import { SettingsCard, Rows, Muted } from "@/components/SettingsCards";
import { FLOAT } from "@/components/SettingsRows";
import { uiAvatar } from "@/components/SidebarProfileCard";
import { useSavedAssets, invalidateSavedAssets } from "@/components/SavedAssets";
import { formatPhoneNumber } from "@/utils/validation";
import { isUserLoggedIn, userHeaders } from "@/utils/userAuth";
import { useAppLive } from "@/utils/appLive";
import { squareDataUrl } from "@/utils/imageResize";
import { toast } from "@/utils/toast";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const EMPTY = { name: "", logo: "", email: "", phone: "", website: "", address: "", city: "", state: "", zip: "" };

// /app/business: a Pro or Enterprise account's own business, in the
// admin/site-settings layout — the logo photo card on the left, one card per
// group on the right, each edited in the shared sheet (X discards, the green
// checkmark saves). Anyone without the plan is sent to Billing.
export default function AppBusiness() {
  const navigate = useNavigate();
  const signedIn = isUserLoggedIn();
  const { hasAccess, business, loading, refresh } = useSavedAssets();
  // The save response carries the fresh business, so the cards repaint from
  // it at once - no waiting on the shared-cache refetch. The override clears
  // as soon as the shared copy catches up.
  const [bizOverride, setBizOverride] = useState(null);
  useEffect(() => {
    // keep the saved copy on screen until the shared cache actually carries it
    setBizOverride((o) => {
      if (!o) return o;
      const b = business || {};
      const caughtUp = Object.keys(o).every((k) => k === "id" || String(b[k] || "") === String(o[k] || ""));
      return caughtUp ? null : o;
    });
  }, [business]);
  const biz = { ...EMPTY, ...(business || {}), ...(bizOverride || {}) };

  const [sheet, setSheet] = useState(null); // { key, title, fields: [[field, label], ...] }
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const [logoMenu, setLogoMenu] = useState({ open: false, event: undefined });
  const logoFileRef = useRef(null);

  useAppLive(() => { if (signedIn) refresh(); }); // a plan change shows up without a reload
  useEffect(() => { if (signedIn) refresh(); }, []); // eslint-disable-line -- always land on fresh data

  useEffect(() => {
    if (loading || hasAccess) return;
    navigate("/app/billing", { replace: true });
  }, [loading, hasAccess, navigate]);

  const put = async (body) => {
    const res = await fetch(`${BACKEND_URL}/api/user/business`, {
      method: "PUT", headers: userHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify(body),
    });
    const d = await res.json().catch(() => ({}));
    if (!res.ok || d.success === false) throw new Error(d.detail || "Could not save");
    if (d.business) setBizOverride(d.business); // repaint now, from the server's copy
    invalidateSavedAssets(); // the sidebar's business card reads the same response
    return d;
  };

  const openSheet = (key, title, fields) => {
    setDraft(Object.fromEntries(fields.map(([f]) => [f, biz[f]])));
    setSheet({ key, title, fields });
  };
  const saveSheet = async () => {
    setSaving(true);
    try {
      await put(draft);
      toast.success("Business updated");
      setSheet(null);
    } catch (err) { toast.error(err.message); } finally { setSaving(false); }
  };

  const onLogoFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!file) return;
    if (!/^image\//.test(file.type)) { toast.error("Use a PNG, JPG, WebP, GIF or SVG."); return; }
    if (file.size > 4 * 1024 * 1024) { toast.error("Image must be under 4 MB."); return; }
    try { await put({ logo: await squareDataUrl(file, 512) }); toast.success("Logo updated"); }
    catch (err) { toast.error(err.message); }
  };
  const removeLogo = async () => {
    try { await put({ logo: "" }); toast.success("Logo removed"); }
    catch (err) { toast.error(err.message); }
  };

  const show = (v) => v || <Muted>Not set</Muted>;

  return (
    <AppLayout>
      <AppSeo title="Business" description="Your business details, shown with your documents and branding." path="/app/business" />
      {loading || !hasAccess ? (
        <div style={{ display: "flex", justifyContent: "center", padding: 60 }}><IonSpinner name="crescent" /></div>
      ) : (
        <div className="admin-profile app-business" style={{ maxWidth: 1200, margin: "0 auto" }}>
          <IonGrid>
            <IonRow>
              {/* Logo: the sidebar's Business row */}
              <IonCol size="12" sizeSm="4">
                <IonCard className="profile-photo-card">
                  <img alt="" src={biz.logo || uiAvatar(biz.name || "Business")} />
                  <IonButtons className="image-buttons">
                    <IonButton size="small" fill="clear" aria-label="Logo options" onClick={(e) => setLogoMenu({ open: true, event: e.nativeEvent })}>
                      <IonIcon slot="icon-only" size="small" icon={ellipsisVerticalOutline} />
                    </IonButton>
                  </IonButtons>
                </IonCard>
                <FreshPopover open={logoMenu.open} event={logoMenu.event} onClose={() => setLogoMenu({ open: false, event: undefined })} side="bottom" alignment="end" className="profile-photo-menu">
                  <IonList lines="none">
                    <IonItem button detail={false} onClick={() => { setLogoMenu({ open: false, event: undefined }); logoFileRef.current?.click(); }}>
                      <IonIcon slot="start" icon={cameraOutline} />
                      <IonLabel>Upload logo</IonLabel>
                    </IonItem>
                    {biz.logo && (
                      <IonItem button detail={false} onClick={() => { setLogoMenu({ open: false, event: undefined }); removeLogo(); }}>
                        <IonIcon slot="start" icon={trashOutline} color="danger" />
                        <IonLabel color="danger">Remove logo</IonLabel>
                      </IonItem>
                    )}
                  </IonList>
                </FreshPopover>
                <input ref={logoFileRef} type="file" accept="image/*" hidden onChange={onLogoFile} />
              </IonCol>

              <IonCol size="12" sizeSm="8">
                <SettingsCard id="section-business" title="Business Info" className="business-card"
                  onEdit={() => openSheet("info", "Business Info", [["name", "Business name"]])}>
                  <Rows rows={[["Name", show(biz.name)], ["Logo", biz.logo ? "Uploaded" : <Muted>Using the initial</Muted>]]} />
                </SettingsCard>

                <SettingsCard id="section-contact" title="Contact"
                  onEdit={() => openSheet("contact", "Contact", [["email", "Email"], ["phone", "Phone"], ["website", "Website"]])}>
                  <Rows rows={[["Email", show(biz.email)], ["Phone", show(biz.phone)], ["Website", show(biz.website)]]} />
                </SettingsCard>

                <SettingsCard id="section-address" title="Address"
                  onEdit={() => openSheet("address", "Address", [["address", "Street address"], ["city", "City"], ["state", "State"], ["zip", "ZIP"]])}>
                  <Rows rows={[["Street", show(biz.address)], ["City", show(biz.city)], ["State", show(biz.state)], ["ZIP", show(biz.zip)]]} />
                </SettingsCard>
              </IonCol>
            </IonRow>
          </IonGrid>
        </div>
      )}

      {sheet && (
        <EditSheet title={sheet.title} onClose={() => setSheet(null)} onSave={saveSheet} saving={saving}>
          {sheet.fields.map(([field, label]) => (
            <IonInput key={field} {...FLOAT} label={label} value={draft[field] ?? ""}
              type={field === "phone" ? "tel" : undefined} inputmode={field === "phone" ? "tel" : undefined}
              onIonInput={(e) => {
                const raw = e.detail.value ?? "";
                const val = field === "phone" ? formatPhoneNumber(raw) : raw;
                if (field === "phone" && e.target) e.target.value = val; // reflect the formatted text at once
                setDraft((p) => ({ ...p, [field]: val }));
              }} />
          ))}
        </EditSheet>
      )}
    </AppLayout>
  );
}
